<div class="p-4" cdkDropListGroup>
  <!-- Week navigation -->
  <div class="mb-4 flex items-center gap-2">
    <div class="join">
      <button type="button" class="btn btn-sm join-item" aria-label="Vorherige Woche" (click)="store.previousWeek()">
        <fa-icon [icon]="faChevronLeft"></fa-icon>
      </button>
      <button type="button" class="btn btn-sm join-item" (click)="store.resetToToday()">Heute</button>
      <button type="button" class="btn btn-sm join-item" aria-label="Nächste Woche" (click)="store.nextWeek()">
        <fa-icon [icon]="faChevronRight"></fa-icon>
      </button>
    </div>
    <span class="text-sm font-medium text-base-content/80">{{ rangeLabel }}</span>
    @if (store.loading()) {
      <span class="loading loading-spinner loading-xs text-base-content/40"></span>
    }
  </div>

  @if (store.error(); as err) {
    <div class="alert alert-error mb-4">
      <span>{{ err }}</span>
      <button type="button" class="btn btn-sm" (click)="store.reload()">Erneut laden</button>
    </div>
  }

  <!-- Calendar + pool. On mobile the pool comes first; on desktop it sits below
       the calendar (flex ordering keeps a single DOM copy for one drop group). -->
  <div class="flex flex-col gap-4">
  <!-- ===================== Desktop grid (day columns × slot rows) ===================== -->
  <div class="hidden lg:block overflow-x-auto lg:order-1">
    <div class="grid min-w-[56rem] grid-cols-[6rem_repeat(7,minmax(0,1fr))] gap-2">
      <!-- Header row: corner + day headers -->
      <div></div>
      @for (day of store.dayKeys(); track day) {
        <div
          class="rounded-lg px-2 py-1 text-center"
          [ngClass]="{ 'bg-primary/10': isToday(day), 'opacity-50': isPast(day) }"
        >
          <div class="text-xs font-semibold uppercase text-base-content/60">{{ formatWeekdayShort(day) }}</div>
          <div class="text-sm font-medium" [class.text-primary]="isToday(day)">{{ formatDayMonth(day) }}</div>
        </div>
      }

      <!-- One row per active/expanded slot -->
      @for (slot of rowSlots; track slot) {
        <div class="flex items-center pr-1 text-right text-xs font-semibold text-base-content/60">
          {{ store.slotLabel(slot) }}
        </div>
        @for (day of store.dayKeys(); track day) {
          <div
            class="group/cell relative flex min-h-16 flex-col rounded-lg border-t border-base-200 p-1 transition-colors"
            [ngClass]="{
              'bg-primary/5': isToday(day),
              'hover:bg-base-200/40': !isToday(day) && !isPast(day),
              'opacity-60': isPast(day),
              'cursor-pointer': isCellEmpty(day, slot)
            }"
            [attr.role]="isCellEmpty(day, slot) ? 'button' : null"
            [attr.tabindex]="isCellEmpty(day, slot) ? 0 : null"
            [attr.aria-label]="isCellEmpty(day, slot) ? a11yAddLabel(day, slot) : null"
            (click)="onEmptyCellClick(day, slot)"
            (keydown.enter)="onEmptyCellClick(day, slot)"
            (keydown.space)="$event.preventDefault(); onEmptyCellClick(day, slot)"
          >
            <div
              cdkDropList
              [cdkDropListData]="{ dateKey: day, slot }"
              (cdkDropListDropped)="onDrop($event)"
              class="flex-1 space-y-1 min-h-8"
            >
              @for (entry of entriesFor(day, slot); track entry.id ?? entry.clientId) {
                <app-meal-entry-card
                  cdkDrag
                  [cdkDragData]="entry"
                  [cdkDragDisabled]="!canDrag(entry)"
                  (cdkDragStarted)="onDragStarted()"
                  (cdkDragEnded)="onDragEnded()"
                  [entry]="entry"
                  [showCreateRecipe]="true"
                  (open)="openRecipe($event)"
                  (edit)="openEdit($event)"
                  (remove)="deleteEntry($event)"
                  (retry)="retry($event)"
                  (createRecipe)="store.convertEntryToRecipe($event)"
                ></app-meal-entry-card>
              }
            </div>

            @if (isCellEmpty(day, slot)) {
              <!-- Empty slot: the whole cell is the add affordance; this "+" is
                   just a visual hint (pointer-events-none keeps drop detection clean). -->
              <div
                class="pointer-events-none absolute inset-0 flex items-center justify-center text-base-content/25 opacity-0 transition-opacity group-hover/cell:opacity-100 group-focus-visible/cell:opacity-100"
              >
                <fa-icon [icon]="faPlus" class="text-xs"></fa-icon>
              </div>
            } @else {
              <button
                type="button"
                class="mt-1 flex w-full items-center justify-center rounded-md py-1 text-base-content/30 opacity-0 transition-opacity hover:bg-base-200 hover:text-base-content/70 group-hover/cell:opacity-100 focus-visible:opacity-100"
                [attr.aria-label]="a11yAddLabel(day, slot)"
                (click)="openPicker(day, slot)"
              >
                <fa-icon [icon]="faPlus" class="text-xs"></fa-icon>
              </button>
            }
          </div>
        }
      }
    </div>

    <!-- Collapsed empty slots: thin affordances -->
    @if (collapsedSlots.length) {
      <div class="mt-2 flex flex-wrap gap-2">
        @for (slot of collapsedSlots; track slot) {
          <button
            type="button"
            class="btn btn-ghost btn-xs text-base-content/50"
            (click)="expandSlot(slot)"
          >
            <fa-icon [icon]="faPlus" class="text-xs"></fa-icon> {{ store.slotLabel(slot) }}
          </button>
        }
      </div>
    }
  </div>

  <!-- ===================== Mobile list (day cards) ===================== -->
  <div class="space-y-3 lg:hidden order-2">
    @for (day of store.dayKeys(); track day) {
      <div
        class="rounded-xl border border-base-200 bg-base-100 p-3"
        [class.opacity-60]="isPast(day)"
        [class.border-primary]="isToday(day)"
      >
        <div class="mb-2 flex items-baseline gap-2">
          <span class="text-sm font-semibold" [class.text-primary]="isToday(day)">{{ formatWeekdayShort(day) }}</span>
          <span class="text-sm text-base-content/70">{{ formatDayMonth(day) }}</span>
          @if (isToday(day)) {
            <span class="badge badge-primary badge-sm">Heute</span>
          }
        </div>

        <div class="space-y-2">
          @for (slot of rowSlots; track slot) {
            <div>
              <div class="mb-1 text-xs font-semibold uppercase tracking-wide text-base-content/50">
                {{ store.slotLabel(slot) }}
              </div>
              <div class="space-y-1">
                <div
                  cdkDropList
                  [cdkDropListData]="{ dateKey: day, slot }"
                  (cdkDropListDropped)="onDrop($event)"
                  class="space-y-1 min-h-8"
                >
                  @for (entry of entriesFor(day, slot); track entry.id ?? entry.clientId) {
                    <app-meal-entry-card
                      cdkDrag
                      [cdkDragData]="entry"
                      [cdkDragDisabled]="!canDrag(entry)"
                      (cdkDragStarted)="onDragStarted()"
                      (cdkDragEnded)="onDragEnded()"
                      [entry]="entry"
                      [showCreateRecipe]="true"
                      (open)="openRecipe($event)"
                      (edit)="openEdit($event)"
                      (remove)="deleteEntry($event)"
                      (retry)="retry($event)"
                      (createRecipe)="store.convertEntryToRecipe($event)"
                    ></app-meal-entry-card>
                  }
                </div>
                <button
                  type="button"
                  class="flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-base-300 py-1.5 text-xs text-base-content/40 hover:text-base-content/70"
                  [attr.aria-label]="a11yAddLabel(day, slot)"
                  (click)="openPicker(day, slot)"
                >
                  <fa-icon [icon]="faPlus" class="text-xs"></fa-icon> Hinzufügen
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- ===================== Meal pool (unplaced candidates) =====================
       First on mobile (order-1), below the calendar on desktop (lg:order-2). -->
  <div class="order-1 lg:order-2 rounded-xl border border-base-200 bg-base-100 p-3 lg:p-4">
    <div class="mb-3 flex items-center justify-between gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <fa-icon [icon]="faLayerGroup" class="text-base-content/60"></fa-icon>
        <span class="font-semibold">Pool</span>
        <span class="hidden truncate text-xs text-base-content/50 sm:inline">
          In einen Tag ziehen, um einzuplanen
        </span>
      </div>
      <div class="flex items-center gap-1">
        <button
          type="button"
          class="btn btn-ghost btn-sm"
          (click)="store.generatePool()"
          [disabled]="store.generating()"
          title="Vorschläge aus Vorrat, Vorlieben und deinen Rezepten generieren"
        >
          @if (store.generating()) {
            <span class="loading loading-spinner loading-xs"></span>
          }
          Vorschläge
        </button>
        <button type="button" class="btn btn-ghost btn-sm" (click)="openPoolPicker()">
          <fa-icon [icon]="faPlus" class="text-xs"></fa-icon> Hinzufügen
        </button>
      </div>
    </div>
    <div
      cdkDropList
      [cdkDropListData]="{ pool: true }"
      cdkDropListOrientation="mixed"
      (cdkDropListDropped)="onDrop($event)"
      class="flex min-h-28 flex-wrap content-start gap-2 lg:min-h-36"
    >
      @for (entry of store.pool(); track entry.id ?? entry.clientId) {
        <div
          class="w-[calc(50%-0.25rem)] sm:w-56"
          cdkDrag
          [cdkDragData]="entry"
          [cdkDragDisabled]="!canDrag(entry)"
          (cdkDragStarted)="onDragStarted()"
          (cdkDragEnded)="onDragEnded()"
        >
          <app-meal-entry-card
            rounded="md"
            [entry]="entry"
            [showCreateRecipe]="true"
            (open)="openRecipe($event)"
            (edit)="openEdit($event)"
            (remove)="deleteEntry($event)"
            (retry)="retry($event)"
            (createRecipe)="store.convertEntryToRecipe($event)"
          ></app-meal-entry-card>
        </div>
      } @empty {
        <div class="flex flex-1 items-center justify-center px-2 py-6 text-center text-sm text-base-content/40">
          Pool ist leer – Vorschläge hinzufügen oder vom Assistenten generieren lassen.
        </div>
      }
    </div>
  </div>
</div>
</div>

<!-- Slot picker (recipe search + free text) -->
<app-slot-picker
  (recipeChosen)="onRecipeChosen($event)"
  (freeTextChosen)="onFreeTextChosen($event)"
  (poolItemChosen)="onPoolItemChosen($event)"
  (recipePooled)="onRecipePooled($event)"
  (freeTextPooled)="onFreeTextPooled($event)"
></app-slot-picker>

<!-- Edit servings / note -->
<ui-modal #editModal [title]="'Eintrag bearbeiten'" size="sm">
  @if (editing(); as entry) {
    <p class="mb-3 truncate text-sm font-medium">{{ entry.title }}</p>
    <label class="mb-1 block text-xs font-medium text-base-content/60">Portionen</label>
    <input
      type="number"
      min="1"
      class="input input-bordered input-sm mb-3 w-full"
      [(ngModel)]="editServings"
      aria-label="Portionen"
    />
    <label class="mb-1 block text-xs font-medium text-base-content/60">Notiz</label>
    <textarea
      class="textarea textarea-bordered w-full"
      rows="2"
      [(ngModel)]="editNote"
      aria-label="Notiz"
    ></textarea>
  }
  <div modal-footer>
    <button type="button" class="btn btn-sm btn-primary" (click)="saveEdit()">Speichern</button>
  </div>
</ui-modal>
